import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, User, Upload, AlertCircle, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import { updateParentProfile } from "@/services/parent";
import { getMasterDropdownService, getCitiesBasedOnState } from "@/services/masters";
import { colors, fontSize, fontWeight, textColor, bgColor, borderColor } from "@/theme";
import FullScreenLocationPicker from "../common/LocationSelector";
import { useCurrentUser } from "@/hooks/useCurrentUser";

const profileSchema = z.object({
  First_Name: z.string().min(1, "First name is required"),
  Last_Name: z.string().min(1, "Last name is required"),
  Phone: z.string().min(10, "Phone must be 10 digits").max(10, "Phone must be 10 digits"),
  Alternative_Phone: z.string().optional(),
  Address: z.string().min(1, "Address is required — pick a location on the map"),
  Latitude: z.string().min(1, "Please select a location on the map"),
  Longitude: z.string().min(1, "Please select a location on the map"),
  State: z.string().min(1, "State is required"),
  City: z.string().min(1, "City is required"),
  Password: z.string().optional(),
  Conform_Password: z.string().optional(),
}).refine(
  (d) => { if (d.Password || d.Conform_Password) return d.Password === d.Conform_Password; return true; },
  { message: "Passwords do not match", path: ["Conform_Password"] }
);

type ProfileFormData = z.infer<typeof profileSchema>;

interface UpdateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  profileData?: any;
  isRequired?: boolean;
}

export default function UpdateProfileModal({
  isOpen, onClose, onSuccess, profileData, isRequired = false,
}: UpdateProfileModalProps) {
  const userDetails = useCurrentUser();
  const profileImageRef = useRef<HTMLInputElement>(null);
  const proofImageRef = useRef<HTMLInputElement>(null);
  const pendingCityRef = useRef<string>("");
  const formInitializedRef = useRef(false);

  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [proofImageFile, setProofImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string>("");
  const [proofImagePreview, setProofImagePreview] = useState<string>("");
  const [mapOpen, setMapOpen] = useState(false);
  const [states, setStates] = useState<{ id: number; label: string; value: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; label: string; value: string }[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const { control, handleSubmit, reset, setValue, watch, setError, clearErrors, formState: { isSubmitting, errors } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      First_Name: "", Last_Name: "", Phone: "", Alternative_Phone: "",
      Address: "", Latitude: "", Longitude: "", State: "", City: "",
      Password: "", Conform_Password: "",
    },
  });
  const watchedCity = watch("City");
  const prevStateRef = useRef("");
  const prevCityRef = useRef("");
  const watchedState = watch("State");
  const lat = watch("Latitude");
  const lng = watch("Longitude");
  const addr = watch("Address");
  const { trail_status } = userDetails || {};

  // Load states on mount
  useEffect(() => {
    setLoadingStates(true);
    getMasterDropdownService().then(res => {
      if (res.status === 200 && res.data) {
        const rawStates: any[] = (res.data as any).States || [];
        setStates(rawStates.map((s: any, idx: number) => ({
          id: s.id ?? idx, label: s.Name, value: s.unique_code || s.Name,
        })));
      }
    }).catch(() => { }).finally(() => setLoadingStates(false));
  }, []);


  // Load cities when state changes; always reset city first
  useEffect(() => {
    const prevState = prevStateRef.current;

    // clear only when user changes state after initial load
    if (
      prevState &&
      prevState !== watchedState
    ) {
      setValue("City", "");
      setValue("Address", "");
      setValue("Latitude", "");
      setValue("Longitude", "");
      clearErrors([
        "City",
        "Address",
        "Latitude",
        "Longitude",
      ]);
    }

    prevStateRef.current = watchedState;

    setCities([]);

    if (!watchedState) {
      pendingCityRef.current = "";
      return;
    }

    clearErrors("State");

    setLoadingCities(true);

    const pending = pendingCityRef.current;
    pendingCityRef.current = "";

    getCitiesBasedOnState(watchedState)
      .then(res => {
        if (res.status === 200 && res.data) {
          const items: any[] =
            (res.data as any)?.Cities ?? [];

          const cityList = items.map(
            (c: any, idx: number) => ({
              id:
                typeof c === "object"
                  ? (c.id ?? idx)
                  : idx,
              label:
                typeof c === "object"
                  ? c.City_Name
                  : c,
              value:
                typeof c === "object"
                  ? (c.unique_code || c.City_Name)
                  : c,
            })
          );

          setCities(cityList);

          if (pending) {
            const code =
              cityList.find(
                c =>
                  c.label === pending ||
                  c.value === pending
              )?.value || pending;

            setValue("City", code);
          }
        }
      })
      .finally(() => setLoadingCities(false));
  }, [watchedState]);

  useEffect(() => {
    const prevCity = prevCityRef.current;

    // clear location only when city actually changes
    if (
      prevCity &&
      prevCity !== watchedCity
    ) {
      setValue("Address", "");
      setValue("Latitude", "");
      setValue("Longitude", "");

      clearErrors([
        "Address",
        "Latitude",
        "Longitude",
      ]);
    }

    prevCityRef.current = watchedCity;
  }, [watchedCity]);

  // Populate form once — wait for states so state code resolves correctly
  useEffect(() => {
    if (!profileData || states.length === 0 || formInitializedRef.current) return;
    formInitializedRef.current = true;
    const stateCode = states.find(s => s.label === profileData.State || s.value === profileData.State)?.value
      || profileData.State_Code || profileData.state_code || profileData.State || "";
    pendingCityRef.current = profileData.City || profileData.city || "";
    reset({
      First_Name: profileData.First_Name || "",
      Last_Name: profileData.Last_Name || "",
      Phone: profileData.Phone?.toString() || "",
      Alternative_Phone: profileData.Alternative_Phone?.toString() || "",
      Address: profileData.Address || "",
      Latitude: profileData.Latitude?.toString() || "",
      Longitude: profileData.Longitude?.toString() || "",
      State: stateCode,
      City: profileData.City_Code || profileData.city_code || profileData.City || "",
      Password: "", Conform_Password: "",
    });
    if (profileData.Profile_Image) setProfileImagePreview(profileData.Profile_Image);
    if (profileData.Proof_Image) setProofImagePreview(profileData.Proof_Image);
  }, [profileData, states, reset]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: "profile" | "proof") => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (type === "profile") { setProfileImageFile(file); setProfileImagePreview(preview); }
    else { setProofImageFile(file); setProofImagePreview(preview); }
  };

  const handleLocationChange = (loc: { latitude: string; longitude: string; address: string }) => {
    setValue("Latitude", loc.latitude, { shouldValidate: true });
    setValue("Longitude", loc.longitude, { shouldValidate: true });
    setValue("Address", loc.address, { shouldValidate: true });
    clearErrors(["Latitude", "Longitude", "Address"]);
  };

  const onSubmit = async (data: ProfileFormData) => {
    clearErrors();
    if (!profileData?.id) { toast.error("Profile ID not found"); return; }

    const stateName = states.find(s => s.value === data.State)?.label || data.State || "";
    const cityName = cities.find(c => c.value === data.City)?.label || data.City || "";

    const formData = new FormData();
    const orig = profileData;

    const append = (key: string, val: any) => {
      if (val !== undefined && val !== null && val !== "") formData.append(key, val);
    };

    if (data.First_Name !== (orig.First_Name || "")) append("First_Name", data.First_Name);
    if (data.Last_Name !== (orig.Last_Name || "")) append("Last_Name", data.Last_Name);
    if (data.Phone !== (orig.Phone?.toString() || "")) append("Phone", data.Phone);
    if ((data.Alternative_Phone || "") !== (orig.Alternative_Phone?.toString() || ""))
      append("Alternative_Phone", data.Alternative_Phone);
    if (data.Address !== (orig.Address || "")) append("Address", data.Address);
    if ((data.Latitude || "") !== (orig.Latitude?.toString() || "")) append("Latitude", data.Latitude);
    if ((data.Longitude || "") !== (orig.Longitude?.toString() || "")) append("Longitude", data.Longitude);

    // Always send state/city with name + code
    append("State", stateName);
    append("State_Code", data.State);
    append("City", cityName);
    append("City_Code", data.City);

    if (data.Password) append("Password", data.Password);
    if (data.Conform_Password) append("Conform_Password", data.Conform_Password);
    if (profileImageFile) formData.append("Profile_Image", profileImageFile);
    if (proofImageFile) formData.append("Proof_Image", proofImageFile);
    formData.append("updated_by", profileData.Username || profileData.username || "");

    try {
      const response = await updateParentProfile(profileData.id, formData, true);
      if (response.status === 200 || response.status === 201) {
        toast.success("Profile updated successfully");
        setProfileImageFile(null);
        setProofImageFile(null);
        onSuccess?.();
      } else if (response.errors && typeof response.errors === "object") {
        Object.entries(response.errors).forEach(([key, value]) => {
          const errorText = Array.isArray(value) ? value[0] : String(value);
          if (["error", "non_field_errors", "detail"].includes(key)) toast.error(errorText);
          else setError(key as keyof ProfileFormData, { type: "server", message: errorText });
        });
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error: any) {
      toast.error(error?.message || "Error updating profile");
    }
  };

  if (!isOpen) return null;

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div>
      <p className={`${fontSize.xs} ${fontWeight.medium} ${textColor.muted} mb-1`}>{label}</p>
      <div className={`px-3 py-2 ${bgColor.gray50} border ${borderColor.gray200} rounded-lg ${fontSize.sm} ${textColor.body}`}>
        {value || "—"}
      </div>
    </div>
  );



  const ImageUpload = ({ label, preview, inputRef, onChange }: {
    label: string; preview: string;
    inputRef: React.RefObject<HTMLInputElement>;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <div>
      <p className={`${fontSize.sm} ${fontWeight.bold} mb-2`}>{label}</p>
      <div
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-lg border-2 border-dashed ${borderColor.gray300} hover:border-brand-400 transition-colors overflow-hidden`}
        style={{ height: 120 }}
      >
        {preview ? (
          <img src={preview} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className={`flex flex-col items-center justify-center h-full gap-2 ${textColor.muted}`}>
            <Upload className="w-6 h-6" />
            <span className={fontSize.xs}>Click to upload</span>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onChange} />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className={`${bgColor.white} rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0" style={{ background: colors.brand.gradient }}>
          <div>
            <h2 className={`${fontSize.xl} ${fontWeight.bold} ${textColor.white}`}>
              {isRequired ? "Complete Your Profile" : "Update Profile"}
            </h2>
            {isRequired && <p className={`${fontSize.xs} text-white/80 mt-0.5`}>Please fill in your details to continue</p>}
          </div>
          {!isRequired && (
            <button onClick={onClose} className={`${textColor.white} hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg transition`}>
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {isRequired && (
          <div className="flex items-start gap-2 mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-600" />
            <p className={`${fontSize.xs} text-yellow-700`}>
              Your profile is incomplete. Fill in all required fields before accessing the dashboard.
            </p>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-6">

          {/* Account Info */}
          <div className={`p-4 ${bgColor.primaryLight} border ${borderColor.primary} rounded-lg`}>
            <div className="flex items-center gap-2 mb-3">
              <User className={`w-4 h-4 ${textColor.primary}`} />
              <span className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.primary}`}>Account Info</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow label="Username (not editable)" value={profileData?.Username || profileData?.username || ""} />
              <InfoRow label="Email (not editable)" value={profileData?.Email || profileData?.email || ""} />
              <InfoRow label="Phone (not editable)" value={profileData?.Phone || profileData?.phone || ""} />
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField control={control} name="First_Name" label="First Name" placeholder="Enter first name" required error={errors.First_Name?.message} onClear={() => clearErrors("First_Name")} />
            <FormField control={control} name="Last_Name" label="Last Name" placeholder="Enter last name" required error={errors.Last_Name?.message} onClear={() => clearErrors("Last_Name")} />
          </div>

          {/* State / City */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Dropdown name="State" control={control} label="State" options={states} placeholder="Select State" isLoading={loadingStates} error={errors.State?.message} onClear={() => clearErrors("State")} required />
            <Dropdown name="City" control={control} label="District" options={cities} placeholder={watchedState ? "Select District" : "Select state first"} disabled={!watchedState} isLoading={loadingCities} error={errors.City?.message} onClear={() => clearErrors("City")} required />
          </div>

          {/* Location */}
          <div>
            <p className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary} mb-2`}>
              Current Location <span className="text-red-500">*</span>
            </p>
            {addr ? (
              <div className={`px-3 py-2.5 ${bgColor.gray50} border ${borderColor.gray200} rounded-lg ${fontSize.sm} ${textColor.body} mb-2 flex items-start gap-2`}>
                <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span>{addr}</span>
              </div>
            ) : errors.Address && (
              <p className="text-xs text-red-500 mb-2">{errors.Address.message}</p>
            )}
            <button
              type="button"
              onClick={() => setMapOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors text-sm font-semibold"
            >
              <MapPin className="w-4 h-4" />
              {lat && lng ? "Change Location" : "Select Location"}
            </button>
          </div>

          {/* Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImageUpload label="Profile Photo" preview={profileImagePreview} inputRef={profileImageRef} onChange={(e) => handleFileChange(e, "profile")} />
            <ImageUpload label="ID / Proof Document" preview={proofImagePreview} inputRef={proofImageRef} onChange={(e) => handleFileChange(e, "proof")} />
          </div>

          {/* Password */}
          <div>
            <p className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary} mb-3`}>
              Change Password{" "}
              <span className={`${fontSize.xs} ${fontWeight.normal} ${textColor.muted}`}>(leave blank to keep current)</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField control={control} name="Password" label="New Password" type="password" placeholder="Enter new password" error={errors.Password?.message} onClear={() => clearErrors("Password")} />
              <FormField control={control} name="Conform_Password" label="Confirm Password" type="password" placeholder="Re-enter new password" error={errors.Conform_Password?.message} onClear={() => clearErrors("Conform_Password")} />
            </div>
          </div>

        </div>

        <div className={`flex gap-3 px-6 py-4 border-t ${borderColor.gray200} flex-shrink-0`}>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className={`flex-1 py-3 rounded-lg ${fontWeight.semibold} ${fontSize.sm} ${textColor.white} transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            style={{ background: colors.brand.gradient }}
          >
            {isSubmitting ? "Saving..." : "Save Profile"}
          </button>
          {!isRequired && (
            <button type="button" onClick={onClose} disabled={isSubmitting} className={`flex-1 py-3 ${bgColor.gray200} hover:bg-gray-300 ${textColor.heading} ${fontWeight.semibold} ${fontSize.sm} rounded-lg transition-colors disabled:opacity-50`}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <FullScreenLocationPicker
        isOpen={mapOpen}
        onClose={() => setMapOpen(false)}
        value={lat && lng ? { latitude: lat, longitude: lng } : undefined}
        onConfirm={(loc) => { handleLocationChange(loc); setMapOpen(false); }}
        selectedState={
          states.find(s => s.value === watchedState)?.label || ""
        }
        selectedCity={
          cities.find(c => c.value === watch("City"))?.label || ""
        } />
      
    </div>
  );
}
